import React, { useState, useEffect } from "react";
import { Grid, Paper, IconButton, Box, Button } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  createSubscriptionAction,
  updateSubscriptionAction,
} from "../../redux/actions/subscription";
import strings from "../../i18n/language";
import CustomSelectField from "../../form/CustomSelectField";
import Fab from "@mui/material/Fab";
import CustomAutocompleteField from "../../form/CustomAutocompleteField";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import AutoFixNormalIcon from "@mui/icons-material/AutoFixNormal";
import CancelIcon from "@mui/icons-material/Cancel";
import CustomDateField from "../../form/CustomDateField";
import dayjs from "dayjs";
import { instance } from "../../axios";

const SubscriptionEdit = () => {
  // State for editable fields
  const navigate = useNavigate();

  const [type, setType] = useState(null);
  const [start, setStart] = useState(null);
  const [company, setCompany] = useState(null);
  const [patient, setPatient] = useState(null);
  const [validity, setValidity] = useState(null);

  const [valid, setValid] = useState(false);
  const subscription = useSelector((state) => state.subscription.subscription);
  const dispatch = useDispatch();

  useEffect(() => {
    if (subscription) {
      setType(subscription.type);
      setStart(subscription.start ? dayjs(subscription.start) : null);
      setValidity(subscription.validity ? dayjs(subscription.validity) : null);
      setCompany(subscription.Company);
      setPatient(subscription.PatientSubscriptions?.[0]?.Patient);
    }
  }, [subscription, dispatch]);

  useEffect(() => {
    if (start && type && validity) {
      setValid(true);
    } else {
      setValid(false);
    }
  }, [start, type, validity]);

  const save = () => {
    if (subscription && subscription.id)
      dispatch(
        updateSubscriptionAction({
          type: type,
          start: start,
          validity: validity,
          id: subscription.id,
          company_id: company?.id,
        })
      );
    else
      dispatch(
        createSubscriptionAction({
          type: type,
          start: start,
          validity: validity,
          Patient: patient,
          company_id: company?.id,
        })
      );
    navigate("/subscriptions");
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
      <Button startIcon={<ArrowBackIcon />} size="small" onClick={() => navigate("/subscriptions")} sx={{ alignSelf: "flex-start", mb: 1, color: "text.secondary" }}>
        Volver
      </Button>
      <Paper
        sx={{
          p: 2,
          width: "80%",
          backgroundColor: "white",
        }}
      >
        <Grid container justifyContent="center" margin="normal" mt={2}>
          <Grid item xs={8} mt={2} display="flex">
            <CustomSelectField
              onChange={setType}
              margin="normal"
              title={strings.LABEL_TYPE}
              id={"type"}
              required
              value={type}
              defaultValues={[
                { value: "individual", label: strings.LABEL_INDIVIDUAL },
                { value: "family", label: strings.LABEL_FAMILY_GROUP },
                { value: "company", label: strings.LABEL_COMPANY },
              ]}
              defaultValue={"individual"}
            ></CustomSelectField>
          </Grid>
          <Grid item xs={8} display="flex">
            <CustomDateField
              value={start}
              onChange={setStart}
              margin="normal"
              isValid={!!start}
              label={strings.LABEL_DATE_START_CONTRACT}
            />
            <IconButton
              onClick={() => {
                setStart(dayjs(new Date()));
              }}
            >
              <AutoFixNormalIcon></AutoFixNormalIcon>
            </IconButton>
          </Grid>
          <Grid item xs={8} display="flex">
            <CustomDateField
              value={validity}
              onChange={setValidity}
              margin="normal"
              isValid={!!validity}
              label={strings.LABEL_DATE_VALIDITY_CONTRACT}
            />
            <IconButton
              disabled={!start}
              onClick={() => {
                setValidity(start);
              }}
            >
              <AutoFixNormalIcon></AutoFixNormalIcon>
            </IconButton>
          </Grid>
          {type === "company" && (
            <Grid item xs={8} mt={2} display="flex">
              <CustomAutocompleteField
                loading={true}
                value={company}
                setValue={setCompany}
                onChange={setCompany}
                margin="normal"
                noOptionsText={strings.MESSAGE_NO_RESULTS}
                label={strings.LABEL_COMPANY}
                labelField="name"
                launch={async (inputValue, callback) => {
                  const companies = await instance.get(
                    `/company?query=${encodeURI(inputValue.toLowerCase())}`
                  );
                  callback(companies.data?.data);
                  return companies.data?.data;
                }}
              ></CustomAutocompleteField>
            </Grid>
          )}
          {type === "family" && (
            <Grid item xs={8} mt={2} display="flex">
              <CustomAutocompleteField
                loading={true}
                value={patient}
                setValue={setPatient}
                onChange={setPatient}
                margin="normal"
                noOptionsText={strings.MESSAGE_NO_RESULTS}
                label={strings.LABEL_HEAD_FAMILY_GROUP}
                labelField="displayName"
                launch={async (inputValue, callback) => {
                  const patients = await instance.get(
                    `/patient?query=${encodeURI(inputValue.toLowerCase())}`
                  );
                  callback(patients.data?.data);
                  return patients.data?.data;
                }}
              ></CustomAutocompleteField>
            </Grid>
          )}
          {type === "individual" && (
            <Grid item xs={8} mt={2} display="flex">
              <CustomAutocompleteField
                loading={true}
                value={patient}
                setValue={setPatient}
                onChange={setPatient}
                margin="normal"
                noOptionsText={strings.MESSAGE_NO_RESULTS}
                label={strings.LABEL_PATIENT_INDIVIDUAL}
                labelField="displayName"
                launch={async (inputValue, callback) => {
                  const patients = await instance.get(
                    `/patient?query=${encodeURI(inputValue.toLowerCase())}`
                  );
                  callback(patients.data?.data);
                  return patients.data?.data;
                }}
              ></CustomAutocompleteField>
            </Grid>
          )}
        </Grid>
      </Paper>
      <div
        style={{
          alignSelf: "flex-end",
          flexDirection: "column",
          display: "flex",
          marginLeft: 10,
        }}
      >
        <Fab color="error" aria-label="cancel" disabled={true}>
          <CancelIcon />
        </Fab>
        <Fab
          color="primary"
          aria-label="add"
          style={{
            marginTop: 10,
          }}
          onClick={save}
          disabled={!valid}
        >
          <SaveIcon />
        </Fab>
      </div>
    </Box>
  );
};

export default SubscriptionEdit;
